/**
 * @file: InspectionManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  CustomIssueInspectionManager,
  CustomIssueInspectionParams,
} from '@euler/app/flows/task/functions/task-detail/inspection/CustomIssueInspectionManager';
import {
  buildInspectionFlowSummary,
  collectInspectionFlowSummary,
} from '@euler/app/flows/task/functions/task-detail/util';
import { InspectedItemLabel, InspectionFlowSummary } from '@euler/model';
import {
  CommonTaskStatus,
  InspectionCategory,
  SiteInspectionType,
} from '@euler/model/enum';
import { Inspection, SiteInspectionStaged } from '@euler/model/task-detail';
import { array2map } from '@euler/utils/array';
import { dateFromValue } from '@euler/utils/datetime';
import produce, { freeze } from 'immer';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ITaskManager } from '../types';
import { InspectionFlowManager } from './InspectionFlowManager';
import { InspectionManagerBase } from './InspectionManagerBase';
import {
  SiteInspectionManager,
  SiteInspectionParams,
} from './SiteInspectionManager';

export type SiteInspectionContext<T extends Inspection> = {
  manager: SiteInspectionManager<T>;
  subscription: Subscription;
};

export class InspectionManager<
  T extends Inspection,
> extends InspectionManagerBase<T> {
  readonly flowManager = new InspectionFlowManager<T>(this);
  readonly siteInspectionContextMap = new Map<
    number,
    SiteInspectionContext<T>
  >();
  readonly siteInspections$ = new BehaviorSubject(
    new Map<number, SiteInspectionStaged>(),
  );
  readonly siteIdsBeingCommitted$ = new BehaviorSubject(new Set<number>());

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(category: InspectionCategory, taskManager: ITaskManager) {
    super(category, taskManager);
  }

  async addInspectionFlow(params: {
    templateId: number;
    assignToMemberId: number;
  }) {
    await this.flowManager.addInspectionFlow(params);
  }

  async beginInspectionFlow(id: string) {
    await this.flowManager.beginInspectionFlow(id);
  }

  async finishInspectionFlow(
    id: string,
    summary: InspectionFlowSummary,
    options: {
      technicianId: number;
      technicianName: string;
      labels?: InspectedItemLabel[];
      remark?: string;
    },
  ) {
    await this.flowManager.finishInspectionFlow(id, summary, options);
  }

  async finishInspections(options: {
    technicianId: number;
    technicianName: string;
    labels?: InspectedItemLabel[];
    remark?: string;
  }) {
    const detail = this.cachedDetail;

    const { versions, timestamp } = await this.taskService.finishInspections(
      this.taskNo,
      this.category,
      options,
    );

    this.produceWithVersions(versions, (_, { inspection }) => {
      for (const flow of inspection.flows) {
        if (flow.status !== 'finished') {
          const flowId = flow.id;
          flow.status = 'finished';
          flow.finishedAt = dateFromValue(timestamp);
          flow.resultSummary = buildInspectionFlowSummary(s => {
            detail.inspection.sites
              .filter(x => !x.flowId || x.flowId === flowId)
              .forEach(x => {
                collectInspectionFlowSummary(s, x.items);
              });
            (
              detail.inspection.customIssues?.filter(
                x => !x.flowId || x.flowId === flowId,
              ) ?? []
            ).forEach(x => {
              collectInspectionFlowSummary(s, [x]);
            });
          });
        }
      }
      inspection.finishedAt = dateFromValue(timestamp);
      inspection.finishedBy = options.technicianName;
      inspection.status = CommonTaskStatus.Finished;
    });
  }

  async requestAmendInspectionFlow(id: string) {
    await this.flowManager.requestAmendInspectionFlow(id);
  }

  async cancelInspectionFlow(id: string) {
    await this.flowManager.cancelInspectionFlow(id);
  }

  siteInspectionWillCommit(siteId: number) {
    const current = this.siteIdsBeingCommitted$.getValue();
    const next = produce(freeze(current), draft => {
      draft.add(siteId);
    });
    this.siteIdsBeingCommitted$.next(next);
  }

  siteInspectionDidCommit(siteId: number) {
    const current = this.siteIdsBeingCommitted$.getValue();
    const next = produce(freeze(current), draft => {
      draft.delete(siteId);
    });
    this.siteIdsBeingCommitted$.next(next);
  }

  dispose() {
    for (const context of this.siteInspectionContextMap.values()) {
      context.subscription.unsubscribe();
      context.manager.dispose();
    }
    this.siteInspectionContextMap.clear();
    this.flowManager.dispose();
  }

  createCustomIssueInspectionManager(params: CustomIssueInspectionParams) {
    return new CustomIssueInspectionManager<T>(this, params);
  }

  /**
   * Begin inspection for the given site.
   */
  beginSiteInspection(params: Readonly<SiteInspectionParams>) {
    const siteId = params.site.id;
    let context = this.siteInspectionContextMap.get(siteId);
    if (!context) {
      const manager = new SiteInspectionManager<T>(this, params);
      const subscription = manager.staged$.subscribe(staged => {
        const current = freeze(this.siteInspections$.value);
        const siteInspections = produce(current, draft => {
          // update site inspection with the updated staged data.
          draft.set(siteId, staged);
        });
        this.siteInspections$.next(siteInspections);
      });
      context = { manager, subscription };
      this.siteInspectionContextMap.set(siteId, context);
    }
    return context.manager;
  }

  endSiteInspection(siteId: number) {
    const context = this.siteInspectionContextMap.get(siteId);
    if (!context) {
      return;
    }
    const current = freeze(this.siteInspections$.value);
    const nextState = produce(current, draft => {
      draft.delete(siteId);
    });
    this.siteInspections$.next(nextState);
    context.subscription.unsubscribe();
    this.siteInspectionContextMap.delete(siteId);
  }

  async cancelSiteInspection(siteId: number) {
    const context = this.siteInspectionContextMap.get(siteId);
    if (!context) {
      return;
    }

    const resp = await this.taskService.cancelSiteInspection(
      this.taskNo,
      siteId,
    );

    this.handleVersionedResponse(resp, (_, { inspection, original }) => {
      const index = original.sites.findIndex(x => x.siteId === siteId);
      if (index < 0) return;
      inspection.sites.splice(index, 1);
    });
  }

  getSiteInspectionManager(
    siteId: number,
  ): SiteInspectionManager<T> | undefined {
    return this.siteInspectionContextMap.get(siteId)?.manager;
  }

  endSiteInspectionBundle(bundle: string) {
    const current = freeze(this.siteInspections$.value);
    const nextState = produce(current, draft => {
      for (const [siteId, value] of this.siteInspectionContextMap) {
        if (value.manager.params.bundle === bundle) {
          draft.delete(siteId);
          value.subscription.unsubscribe();
          this.siteInspectionContextMap.delete(siteId);
        }
      }
    });
    this.siteInspections$.next(nextState);
  }

  async commitSiteInspectionBundle(
    bundle: string,
    inspectionType: SiteInspectionType,
  ) {
    const siteInspectionManagers = [...this.siteInspectionContextMap.values()]
      .filter(x => x.manager.params.bundle === bundle)
      .map(x => x.manager);
    const payloads = siteInspectionManagers.map(x => x.getCommitPayload());
    const res = await this.taskService.batchCommitSiteInspections(this.taskNo, {
      inspectionType,
      payloads,
    });
    const resultMap = array2map(res.response, x => x.siteId);
    this.handleVersionedResponse(res, (_, { inspection, original }) => {
      for (const siteInspectionManager of siteInspectionManagers) {
        const result = resultMap.get(siteInspectionManager.params.site.id);
        if (result) {
          siteInspectionManager.beginApplyCommitResult(
            result,
            inspection,
            original,
            {
              versions: res.versions,
              response: result,
              timestamp: res.timestamp,
            },
          );
        }
      }
    });
    for (const siteInspectionManager of siteInspectionManagers) {
      const result = resultMap.get(siteInspectionManager.params.site.id);
      if (result) {
        siteInspectionManager.endApplyCommitResult(result);
      }
    }
  }

  resetSiteInspectionBundle(bundle: string) {
    const siteInspectionManagers = [...this.siteInspectionContextMap.values()]
      .filter(x => x.manager.params.bundle === bundle)
      .map(x => x.manager);
    for (const siteInspectionManager of siteInspectionManagers) {
      siteInspectionManager.reset();
    }
  }

  async requestAmendSiteInspections() {
    const { versions, timestamp } =
      await this.taskService.requestAmendInspectionResults(
        this.taskNo,
        this.category,
      );
    this.produceWithVersions(versions, (_, { inspection }) => {
      for (const flow of inspection.flows) {
        flow.status = 'inprogress';
        flow.finishedAt = undefined;
        flow.updatedAt = dateFromValue(timestamp);
      }
      inspection.status = CommonTaskStatus.InProgress;
      inspection.finishedAt = undefined;
      inspection.finishedBy = undefined;
    });
  }
}
